import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= 6) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 font-body">
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-10 text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#114A31] mx-auto" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#1C1C1C]">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-[#595D5A]">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#22C55E] mx-auto" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#1C1C1C]" data-testid="payment-success">
              You're all set!
            </h1>
            <p className="mt-2 text-sm text-[#595D5A]">
              Your <span className="font-medium text-[#114A31]">{info?.tier_name}</span> plan is now active.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-6 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-8 py-6"
              data-testid="go-dashboard-btn"
            >
              Go to dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="h-12 w-12 text-[#EF4444] mx-auto" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#1C1C1C]">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-sm text-[#595D5A]">
              {state === "timeout"
                ? "We couldn't confirm your payment yet. Check your dashboard shortly."
                : "Your payment was cancelled or failed. You can try again from the pricing page."}
            </p>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="mt-6 rounded-full border-[#E5E5E5] px-8 py-6 text-[#1C1C1C]"
            >
              Back to home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
